/**
 * 数据标签旧→新字段归一化纯函数。
 *
 * @see types.ts
 */

import { ChartType, type TChartType } from '@dbl/core/chart';

import type { IDataLabelCompatFields } from './types';

/** 旧版「展示数值」视为开启数据标签的取值 */
const LEGACY_SHOW_VALUES_ON = new Set(['all', '7']);

/**
 * 缺省 labelContent（百分比轴图默认 percent，其余 metric）
 */
export function getDefaultLabelContent(chartType: TChartType | string): string[] {
  const isPercentAxis =
    chartType === ChartType.PercentLine
    || chartType === ChartType.PercentStackBar;
  return isPercentAxis ? ['percent'] : ['metric'];
}

/** 计算当前图类型下 labelContent 合法取值（与设置面板可选项同源） */
export function getAllowedLabelContentValues(params: {
  chartType: TChartType | string | undefined;
  dimensionCount: number;
  metricCount: number;
}): string[] {
  const { chartType, dimensionCount, metricCount } = params;
  const values: string[] = [];

  if (
    chartType === ChartType.PercentStackBar
    || chartType === ChartType.PercentLine
  ) {
    values.push('percent');
  }
  if (metricCount > 0) {
    values.push('metric');
  }
  if (
    (dimensionCount > 1 && chartType !== ChartType.MixedChart)
    || chartType === ChartType.Map
  ) {
    values.push('dimension');
  }
  if (chartType === ChartType.StackBar && dimensionCount > 1) {
    values.push('total');
  }
  return values;
}

export interface IEnsureLabelContentParams {
  labelContent: unknown;
  showDataLabel?: boolean;
  chartType: TChartType | string;
  dimensionCount: number;
  metricCount: number;
}

/**
 * 按当前图类型过滤 labelContent；若开关开启且过滤后为空则补默认。
 *
 * @returns changed=false 表示无需写回
 */
export function ensureLabelContentForChart(
  params: IEnsureLabelContentParams,
): { next: string[]; changed: boolean } {
  const {
    labelContent,
    showDataLabel,
    chartType,
    dimensionCount,
    metricCount,
  } = params;

  const allowedList = getAllowedLabelContentValues({
    chartType,
    dimensionCount,
    metricCount,
  });
  const allowed = new Set(allowedList);
  const prev = Array.isArray(labelContent)
    ? (labelContent as string[])
    : [];
  let next = prev.filter(item => allowed.has(item));

  if (showDataLabel === true && next.length === 0) {
    const fallback = getDefaultLabelContent(chartType);
    if (fallback[0] && allowed.has(fallback[0])) {
      next = fallback;
    } else if (allowedList.length > 0) {
      next = [allowedList[0]];
    }
  }

  const changed =
    next.length !== prev.length
    || next.some((item, index) => item !== prev[index]);

  // 未变化时回传原引用，避免调用方误判「总是新数组」
  return { next: changed ? next : prev, changed };
}

/**
 * 将旧 `showValues` 补齐到新字段；已有新字段优先保留（按字段粒度）。
 *
 * - `showDataLabel` 已是 boolean：保留（含显式 `false`）
 * - `showDataLabel` 缺省：由 `showValues === 'all'|'7'` 推导
 * - `displayForm` 缺省/空串：`showValues === 'all'` → `'all'`，否则 `'adaptive'`
 * - `showDataLabel===true` 且 `labelContent` 非数组/空：补 `getDefaultLabelContent`
 *
 * @param config - 含 customizeSetting 切片字段的对象（原地修改）
 * @param chartType - 当前图表类型（决定 labelContent 缺省）
 * @returns 同一 `config` 引用，便于链式使用
 */
export function normalizeDataLabelConfig<T extends IDataLabelCompatFields>(
  config: T,
  chartType: TChartType | string,
): T {
  const { showValues } = config;
  const hasLegacy = showValues !== undefined && showValues !== null;
  const hasNewSwitch = config.showDataLabel !== undefined;

  if (!hasLegacy && !hasNewSwitch) {
    return config;
  }

  const showValuesText = hasLegacy ? String(showValues) : '';

  // boolean（含显式 false）保留；缺省再回落旧 showValues
  if (typeof config.showDataLabel !== 'boolean') {
    // eslint-disable-next-line no-param-reassign
    config.showDataLabel = LEGACY_SHOW_VALUES_ON.has(showValuesText);
  }

  if (!config.displayForm) {
    // eslint-disable-next-line no-param-reassign
    config.displayForm = showValuesText === 'all' ? 'all' : 'adaptive';
  }

  // 开关开启时不允许空 labelContent（切走 percent 等非法项后需兜底）
  if (
    config.showDataLabel
    && (!Array.isArray(config.labelContent) || config.labelContent.length === 0)
  ) {
    // eslint-disable-next-line no-param-reassign
    config.labelContent = getDefaultLabelContent(chartType);
  } else if (!Array.isArray(config.labelContent)) {
    // eslint-disable-next-line no-param-reassign
    config.labelContent = [];
  }

  return config;
}
